import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchBrands } from "../../services/HomePage/FeaturedBrandApi";
import { FaFire, FaStar, FaChevronLeft, FaChevronRight } from "react-icons/fa";

export default function FeaturedBrands() {
  const [brands, setBrands] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef();

  // Detect drawer mode
  const isDrawerOpen = new URLSearchParams(location.search).get("drawer") === "true";

  useEffect(() => {
    const loadBrands = async () => {
      const data = await fetchBrands();
      const filtered = data.filter((brand) => brand.isActive && brand.isPop);
      setBrands(filtered);
    };
    loadBrands();
  }, []);

  // Auto scroll effect - Right to Left continuously
  useEffect(() => {
    if (brands.length === 0) return;
    
    let scrollInterval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        
        // Agar end par aa gaye toh wapas start se (smooth loop)
        if (scrollLeft + clientWidth >= scrollWidth - 5) {
          scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          scrollRef.current.scrollBy({ left: 180, behavior: "smooth" });
        }
      }
    }, 2500);

    return () => clearInterval(scrollInterval);
  }, [brands.length]);

  const handleBrandClick = (brandId) => {
    navigate(`/brand-details/${brandId}?drawer=true`);
  };

  const handleImageError = (brandId) => {
    setImageErrors(prev => ({ ...prev, [brandId]: true }));
  };

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -250, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 250, behavior: "smooth" });
  };

  // Hide page when drawer is open
  if (isDrawerOpen) return null;

  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-1.5 rounded-full shadow-md mb-3">
          <FaFire size={12} className="text-yellow-400" />
          <span className="text-xs font-bold tracking-wide">FEATURED BRANDS</span>
          <FaStar size={12} className="text-yellow-400" />
        </div>
        
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
            Shop by
          </span>{" "}
          <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
            Top Brands
          </span>
        </h2>
        
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Explore premium healthcare products from trusted brands
        </p>
        
        {/* Decorative dots */}
        <div className="flex justify-center gap-2 mt-4">
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-6 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-600"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-300"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-red-200"></div>
        </div>
      </div>

      {/* Brands Slider Section - No Card, Direct Images */}
      {brands.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-pulse">
            <div className="flex justify-center gap-6 flex-wrap">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="w-[120px] h-[120px] bg-gray-200 rounded-xl animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-16 mx-auto mt-2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative group">
          {/* Navigation Buttons */}
          <button
            onClick={scrollLeft}
            className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 z-20 
            bg-white hover:bg-red-500 text-red-500 hover:text-white 
            shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
            opacity-0 group-hover:opacity-100 focus:outline-none"
          >
            <FaChevronLeft size={14} />
          </button>

          <button
            onClick={scrollRight}
            className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 z-20 
            bg-white hover:bg-red-500 text-red-500 hover:text-white 
            shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
            opacity-0 group-hover:opacity-100 focus:outline-none"
          >
            <FaChevronRight size={14} />
          </button>

          {/* Auto Scroll Container - No Cards, Direct Images */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto scroll-smooth hide-scrollbar"
            style={{
              gap: "30px",
              padding: "10px 40px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="group/brand flex-shrink-0 text-center cursor-pointer transform transition-all duration-300 hover:-translate-y-1"
                style={{ width: "120px" }}
                onClick={() => handleBrandClick(brand.id)}
              >
                {/* Direct Image - No Card */}
                <div className="relative w-[120px] h-[120px] rounded-xl overflow-hidden 
                shadow-md hover:shadow-xl transition-all duration-300">
                  
                  {!imageErrors[brand.id] && brand.image ? (
                    <img
                      src={`https://online-tech.in/${brand.image}`}
                      alt={brand.brandName}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover/brand:scale-110"
                      onError={() => handleImageError(brand.id)}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200">
                      <span className="text-red-500 text-2xl font-bold">
                        {brand.brandName?.charAt(0) || "B"}
                      </span>
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-red-600/70 via-transparent to-transparent 
                  opacity-0 group-hover/brand:opacity-100 transition-opacity duration-300" />
                  
                  {/* View Text on Hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/brand:opacity-100 transition-all duration-300">
                    <span className="text-white text-xs font-bold bg-black/50 px-3 py-1 rounded-full">
                      View Details
                    </span>
                  </div>
                </div>
                
                {/* Brand Name */}
                <p className="text-sm font-semibold text-gray-700 group-hover/brand:text-red-600 
                transition-colors duration-300 mt-2">
                  {brand.brandName}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trust Badges */}
      {brands.length > 0 && (
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[10px] font-bold">✓</span>
              </div>
              <span>100% Genuine Products</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[10px] font-bold">⚡</span>
              </div>
              <span>Best Price Guarantee</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[10px] font-bold">🏆</span>
              </div>
              <span>Premium Quality</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-white/50 rounded-lg p-2 hover:bg-white transition-all duration-300">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[10px] font-bold">❤️</span>
              </div>
              <span>10L+ Happy Customers</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        /* Hide scrollbar for Chrome, Safari and Opera */
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        /* Hide scrollbar for Firefox */
        .hide-scrollbar {
          scrollbar-width: none;
        }
        
        /* Hide scrollbar for IE/Edge */
        .hide-scrollbar {
          -ms-overflow-style: none;
        }
      `}</style>
    </div>
  );
}